import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useAddUsersToMailListMutation,
  useListMailUsersQuery,
} from "api/memberPortal/mailList/mailList.api";
import { MailAccount } from "api/memberPortal/mailList/mailList.types";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";
import { useDebouncedValue } from "hooks/useDebouncedValue";

import AddMemberChips from "./AddMember.chips";
import AddMemberList from "./AddMember.list";
import CreateMemberForm from "./components/CreateMember/CreateMember.form";

const PAGE_SIZE = 50;
const EMPTY_PAGINATED_DATA = { results: [], count: 0 };

export default function AddMember() {
  const navigate = useNavigate();
  const { id } = useParams();
  const mailListId = Number(id);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [selectedMembers, setSelectedMembers] = useState<MailAccount[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const hasSearchQuery = debouncedSearch.trim().length > 0;

  const {
    data: availableUsers = EMPTY_PAGINATED_DATA,
    isLoading: isLoadingUsers,
    isFetching,
  } = useListMailUsersQuery(
    hasSearchQuery
      ? {
          limit: PAGE_SIZE,
          exclude_list: mailListId,
          search: debouncedSearch,
        }
      : skipToken,
  );

  const [addUsers, { isLoading: isSubmitting }] =
    useAddUsersToMailListMutation();

  const displayUsers = useMemo(
    () => availableUsers?.results ?? [],
    [availableUsers],
  );

  const handleToggleUser = (user: MailAccount) => {
    setSelectedMembers((prev) => {
      const exists = prev.some((m) => m.id === user.id);
      if (exists) {
        return prev.filter((m) => m.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleUserCreated = (user: MailAccount) => {
    setSelectedMembers((prev) => {
      if (prev.some((m) => m.id === user.id)) return prev;
      return [...prev, user];
    });
    setShowCreateForm(false);
  };

  const handleClose = () => {
    navigate("..");
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    try {
      await addUsers({
        mailListId,
        userIds: selectedMembers.map((member) => member.id),
      }).unwrap();

      handleClose();
    } catch {
      setSubmitError("An unexpected error occurred");
    }
  };

  const removeMember = (userId: number) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  const resultCount = displayUsers.length;
  const totalCount = availableUsers.count;
  const showingLimited = resultCount < totalCount;

  const searchStatusText = !hasSearchQuery
    ? "Type to search for users"
    : showingLimited
      ? `Showing ${resultCount} of ${totalCount}`
      : `${totalCount} result${totalCount !== 1 ? "s" : ""}`;

  const selectionText =
    selectedMembers.length === 0
      ? "No members selected"
      : `${selectedMembers.length} member${selectedMembers.length !== 1 ? "s" : ""} selected`;

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        Add Members
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={1.5}>
          <SearchBar
            onSearchChange={(value) => setSearch(value)}
            sx={{ pt: 2 }}
          />

          <Typography variant="caption" sx={{ color: "text.secondary", px: 1 }}>
            {searchStatusText}
          </Typography>

          <Link
            component="button"
            variant="caption"
            underline="hover"
            onClick={() => setShowCreateForm((prev) => !prev)}
            sx={{
              display: showCreateForm ? "none" : "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
          >
            <PersonAddAltIcon sx={{ fontSize: 14 }} />
            Create new user
          </Link>
        </Stack>

        {showCreateForm && (
          <CreateMemberForm
            onClose={() => setShowCreateForm(false)}
            onUserCreated={handleUserCreated}
          />
        )}

        <AddMemberList
          isLoadingUsers={isLoadingUsers}
          isFetching={isFetching}
          selectedUsers={selectedMembers}
          onToggleUser={handleToggleUser}
          hasSearchQuery={hasSearchQuery}
          displayUsers={displayUsers}
        />
        <AddMemberChips
          selectedMembers={selectedMembers}
          onRemove={removeMember}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {selectionText}
        </Typography>

        <Stack direction="row" spacing={1}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedMembers.length === 0 || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            + Add to list
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
